import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Grid } from '@mui/system';
import { isEmpty } from 'lodash';
import { VALUATIONS_PAGE_KEY, VALUATIONS_PAGE_VALUE } from 'common/constants/notes';
import { VALUATIONS_SLUG } from 'common/constants/valuations';
import { useFormat } from 'common/hooks';
import { ConfigurationSpreadsheets } from 'common/types/scalarSpreadsheet';
import { ValuationsApproach } from 'common/types/valuation';
import { CustomContainer } from 'components';
import { USD_THOUSANDS } from 'components/FeaturedSpreadsheet/constants';
import CurrencyChipContext from 'components/FeaturedSpreadsheet/context/CurrencyChipContext';
import DCFSummaryTable from 'pages/ValuationsAllocation/approaches/DiscountCashFlow/DCFSummaryTable';
import FinancialsDCFTable from 'pages/ValuationsAllocation/approaches/DiscountCashFlow/DCFTable';
import DCFTerminalValueTable from 'pages/ValuationsAllocation/approaches/DiscountCashFlow/DCFTerminalValueTable';
import { Note } from 'pages/ValuationsAllocation/types';
import { DCFRowConfigs, getApproachTableName, updateDCFOptions } from 'pages/ValuationsAllocation/util';
import ValuationContext from 'pages/ValuationsAllocation/ValuationContext';
import { useNotes } from 'services/hooks/notes';
import DCFApproachContext from './context/DCFApproachContext';
import ebitdaRowConfig from './dcfTerminalValue/config/MultipleTerminal/ebitdaRowConfig';
import revenueRowConfig from './dcfTerminalValue/config/MultipleTerminal/revenueRowConfig';
import revenueAndEbitdaRowConfig from './dcfTerminalValue/config/RevEbitdaMultiple/rowConfig';
import { DCFTableData, DiscountCashFlowProps } from './types';
import {
  DCF_FINANCIALS_TABLE_NAME,
  DCF_SLUG,
  DCF_SUMMARY_TABLE_NAME,
  DCF_TERMINAL_VALUE_TABLE_NAME,
} from './utilities/constants';
import WidgetDrawer from '../../../../components/Drawer/widget-drawer/WidgetDrawer';
import { WidgetDrawerApplicationContext } from '../../../../components/Drawer/widget-drawer/WidgetDrawer.props';

const VALUATION_DCF_FINANCIALS_CONTAINER_CLASS = `${VALUATIONS_SLUG}-${DCF_SLUG}-${DCF_FINANCIALS_TABLE_NAME}-container`;
const VALUATION_DCF_SUMMARY_CONTAINER_CLASS = `${VALUATIONS_SLUG}-${DCF_SLUG}-${DCF_SUMMARY_TABLE_NAME}-container`;
const VALUATION_DCF_TERMINAL_VALUE_CONTAINER_CLASS = `${VALUATIONS_SLUG}-${DCF_SLUG}-${DCF_TERMINAL_VALUE_TABLE_NAME}-container`;

const DiscountCashFlow = (props: DiscountCashFlowProps) => {
  const { spreadsheets, onChange, workbook, name, approaches } = props;
  const dcfSpreadsheet = spreadsheets.dcf;
  const tableData = dcfSpreadsheet.tableData as DCFTableData;
  const { approach, isDisabled } = tableData;
  const approachPanelId = approach.panelId;
  const [tvtSelectedApproach, setTvtSelectedApproach] = useState<ValuationsApproach | undefined>({});

  const [format, formatDispatch] = useFormat();
  const [globalCurrency, setGlobalCurrency] = useState(USD_THOUSANDS);
  const [currentTerminalValue, setCurrentTerminalValue] = useState<ConfigurationSpreadsheets>();
  const { setNotesInApproach } = useContext(ValuationContext);
  const { notes, setNotes, notesHasChanged, onAddNote, onUpdateNotes, onDeleteNote } = useNotes();

  const benchmarkSpreadsheet = useMemo(() => {
    if (tvtSelectedApproach) {
      const workbookName = getApproachTableName({ approach: tvtSelectedApproach });
      return workbook[workbookName];
    }
    return workbook?.benchmark;
  }, [workbook, tvtSelectedApproach]);

  const tableDataBenchmarkSpreadsheet = benchmarkSpreadsheet?.tableData as DCFTableData;

  const updateCurrentTerminalValueTable = useCallback(
    (tvt: ConfigurationSpreadsheets) => {
      tvt.reverseShouldValidate();
      if (currentTerminalValue) {
        currentTerminalValue.reverseShouldValidate();
      }
      setCurrentTerminalValue(tvt);
    },
    [currentTerminalValue]
  );

  // eslint-disable-next-line
  useEffect(() => {
    if (benchmarkSpreadsheet) {
      const rowConfigsObject = {
        ebitdaRowConfig,
        revenueAndEbitdaRowConfig,
        revenueRowConfig,
      } as DCFRowConfigs;

      updateDCFOptions({
        DCFSpreadsheets: spreadsheets,
        benchmarkApproach: tableDataBenchmarkSpreadsheet?.approach,
        DCFRowConfigs: rowConfigsObject,
        approaches,
      });
    }
  }, [approaches, benchmarkSpreadsheet, spreadsheets, tableDataBenchmarkSpreadsheet]);

  useEffect(() => {
    if (!isEmpty(notes) && setNotesInApproach) {
      setNotesInApproach((prevState: Note[] = []) => {
        const tmpNotes = prevState.filter(note => note.panelId !== approachPanelId);

        return [
          ...tmpNotes,
          {
            notes: notes ?? undefined,
            panelId: approachPanelId,
            notesHasChanged,
          },
        ];
      });
    }
  }, [notes, notesHasChanged, approachPanelId, setNotesInApproach]);

  const commonProps = {
    format,
    formatDispatch,
    spreadsheets,
    onChange,
    workbook,
    name,
    terminalValue: approach?.valuations_approach_dcf?.terminal_value,
    isDisabled,
    approach,
    approaches,
    setTvtSelectedApproach,
  };

  const dcfContextValue = useMemo(
    () => ({
      panelId: approach?.panelId,
      format,
      onChange,
      formatDispatch,
      currentTerminalValue,
      setCurrentTerminalValue: updateCurrentTerminalValueTable,
    }),
    [approach, format, formatDispatch, currentTerminalValue, updateCurrentTerminalValueTable, onChange]
  );

  const chipContextValue = useMemo(
    () => ({
      globalCurrency,
      setGlobalCurrency,
    }),
    [globalCurrency, setGlobalCurrency]
  );

  return (
    <DCFApproachContext.Provider value={dcfContextValue}>
      <CurrencyChipContext.Provider value={chipContextValue}>
        <CustomContainer>
          {/* Financials DCF */}
          <Grid className={VALUATION_DCF_FINANCIALS_CONTAINER_CLASS} size={{ xs: 12 }}>
            <FinancialsDCFTable {...commonProps} />
          </Grid>

          {/* DCF summary */}
          <Grid className={VALUATION_DCF_SUMMARY_CONTAINER_CLASS}>
            <DCFSummaryTable {...commonProps} />
          </Grid>

          {/* Terminal value */}
          <Grid className={VALUATION_DCF_TERMINAL_VALUE_CONTAINER_CLASS}>
            <DCFTerminalValueTable {...commonProps} />
          </Grid>

          <WidgetDrawer
            applicationContext={WidgetDrawerApplicationContext.DCF}
            notesProps={{
              pageType: VALUATIONS_PAGE_VALUE,
              pageTypeKey: VALUATIONS_PAGE_KEY,
              pageTypeId: approach.id,
              notes,
              isApproach: true,
              setNotes,
              onAddNote,
              onUpdateNotes,
              onDeleteNote,
              isDisabled,
            }}
          />
        </CustomContainer>
      </CurrencyChipContext.Provider>
    </DCFApproachContext.Provider>
  );
};

export default DiscountCashFlow;
